import useMetrics from "../../../hooks/useMetrics"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { campaignTagsEnum, IPlacementData, IPlacementDataTable } from "../types/placement"
import { placementsEnum } from "../types/types"
import { copy } from "../../../helper/system.helpers.functions"
import { getNewBidAdjustment } from "../services/calcMetrics.service"
import { IPlacementDataForUpdate } from "../store/types/placements"
import { ICampaign, IDefaultTargetAcos } from "../types/sponsoredProducts"
import { matchTypesEnum, matchTypesFromAmazon } from "../../../types/customers"
import { selectCampaignIfNeeded } from "./helpers/placementMetrics.helper"
import { findAovValue } from "./helpers/aov.helper"

const AUDIENCE_MIN_BID_ADJUSTMENT = 0
const AUDIENCE_MAX_BID_ADJUSTMENT = 900

interface ICalcLimitedCPCForPlacement {
  placements: IPlacementDataTable[]
  minLimitedCPC: number
  maxLimitedCPC: number
}

interface IUsePlacementMetricsProps {
  setPlacementsDataForUpdate: React.Dispatch<React.SetStateAction<IPlacementDataForUpdate[]>>
  setSelectedCampaignIds: React.Dispatch<React.SetStateAction<number[]>>
  setMinLimitedCPCByCampaignIdMap: React.Dispatch<React.SetStateAction<Record<string, number>>>
}

const usePlacementMetrics = (params: IUsePlacementMetricsProps) => {
  const { setPlacementsDataForUpdate, setSelectedCampaignIds, setMinLimitedCPCByCampaignIdMap } =
    params

  const { campaignData } = useTypedSelector((select) => select.sponsoredProductsSKC)

  const { calcLimitedCPC, calcTargetCPC, calcBestCPC, determinePercentage } = useMetrics()

  function getAovValue(campaignName: string) {
    return findAovValue(campaignData.getAOVData?.aovs, campaignName)
  }

  function findCampaign(campaignId: number) {
    return campaignData.campaigns.find((campaign) => campaign.campaignId === campaignId)
  }

  function calcBestCPCForPlacement(placements: IPlacementDataTable[]): IPlacementDataTable[] {
    const placementsCopy = JSON.parse(JSON.stringify(placements))

    for (const placement of placementsCopy) {
      if (isPlacementForCalcMetrics(placement.placement, placement.clicks)) {
        placement.bestCPC = calcBestCPC({
          targetCVRCPC: placement.targetCVRCPC,
          targetCPC: placement.targetCPC,
          bestCVRCPC: placement.bestCVRCPC,
        })
      }
    }

    return placementsCopy
  }

  function calcTargetCPCForPlacement(
    rowPlacement: IPlacementData[],
    campaignId: number,
    minLimitedCPC: number
  ) {
    const campaign = findCampaign(campaignId)
    const aovValue = getAovValue(campaign?.campaignName || "")

    const placements: IPlacementDataTable[] = JSON.parse(JSON.stringify(rowPlacement))
    for (const placement of placements) {
      if (isPlacementForCalcMetrics(placement.placement, placement.clicks)) {
        placement.targetCPC = calcTargetCPC({
          targetAcos: placement.targetAcos,
          orders: placement.orders,
          impressions: placement.impressions,
          aovValue: aovValue,
          clicks: placement.clicks,
          bidInAmazon: placement.targetCPCAmazon || 0,
          currentAcos: placement.acos,
          sales: placement.sales,
          defaultBid: campaignData.defaultBid || 0,
        })

        if (placement.orders === 0 && placement.clicks < 4) {
          placement.targetCPC =
            minLimitedCPC *
            (1 +
              determinePercentage(placement, {
                TOSPercentage: campaignData.TOSPercentage,
                PPPercentage: campaignData.PPPercentage,
                ROSPercentage: campaignData.ROSPercentage,
                ABPercentage: campaignData.ABPercentage,
                ADPercentage: campaignData.ADPercentage,
              }) /
                100)
        }
      }
    }

    return placements
  }

  function fillMinCPCPlacementWithoutBids(
    placementDataTable: IPlacementDataTable[],
    minLimitedCPC: number
  ) {
    const copyPlacementDataTable: IPlacementDataTable[] = copy(placementDataTable)
    copyPlacementDataTable.forEach((placement) => {
      if (
        (placement.placement === placementsEnum.AMAZON_BUSINESS ||
          placement.placement === placementsEnum.AUDIENCE) &&
        placement.clicks < 4
      ) {
        const newBidAdjustment = getNewBidAdjustment(placement, minLimitedCPC) || 0
        const cpc = Number(newBidAdjustment) > 0 ? placement.targetCPCAmazon || 0 : minLimitedCPC
        const topModifire =
          cpc *
          (1 +
            determinePercentage(placement, {
              TOSPercentage: campaignData.TOSPercentage,
              PPPercentage: campaignData.PPPercentage,
              ROSPercentage: campaignData.ROSPercentage,
              ABPercentage: campaignData.ABPercentage,
              ADPercentage: campaignData.ADPercentage,
            }) /
              100)
        placement.limitedCPC = placement.orders === 0 ? Number(topModifire.toFixed(2)) : cpc
        placement.targetCPC = placement.orders === 0 ? Number(topModifire.toFixed(2)) : cpc
      }
    })

    return copyPlacementDataTable
  }

  function calcLimitedCPCForPlacement(
    placements: IPlacementDataTable[],
    campaignId: number
  ): ICalcLimitedCPCForPlacement {
    const campaign = findCampaign(campaignId)
    const aovValue = getAovValue(campaign?.campaignName || "")
    const placementsCopy: IPlacementDataTable[] = copy(placements)

    let minLimitedCPC = Number.POSITIVE_INFINITY
    let maxLimitedCPC = Number.NEGATIVE_INFINITY
    const indexesPlacementForPlacementModifireFromSettings: number[] = []
    let indexPlacementWithMinCPC: number | null = null

    for (let i = 0; i < placementsCopy.length; i++) {
      const placement = placementsCopy[i]

      if (isPlacementForCalcMetrics(placement.placement, placement.clicks)) {
        const isCampaignsForCalcBidByRank = !!(campaign && campaign.rank)
        const isBidReductionCampaign = !!(
          isCampaignsForCalcBidByRank &&
          (campaign.rank.lastDay <= 4 ||
            campaign.rank.avgLastWeek - campaign.rank.lastDay < 3 ||
            campaign.rank.avgLastWeek === campaign.rank.lastDay)
        )
        if (isBidReductionCampaign) {
          placement.limitedCPC = placement.targetCPCAmazon
            ? Math.max(placement.targetCPCAmazon - 0.1, 0)
            : 0
        } else {
          placement.limitedCPC = calcLimitedCPC({
            defaultBid: campaignData.defaultBid || 0,
            clicks: placement.clicks,
            conditions: campaignData.limitedCPCValue?.conditions || [],
            currentAcos: placement.acos,
            targetAcos: placement.targetAcos,
            orders: placement.orders,
            impressions: placement.impressions,
            aovValue: aovValue,
            bidInAmazon: placement.targetCPCAmazon || 0,
            sales: placement.sales,
            percentage: determinePercentage(placement, {
              TOSPercentage: campaignData.TOSPercentage,
              PPPercentage: campaignData.PPPercentage,
              ROSPercentage: campaignData.ROSPercentage,
              ABPercentage: campaignData.ABPercentage,
              ADPercentage: campaignData.ADPercentage,
            }),
            minTargetCPC: campaignData.minTargetCPC,
            maxTargetCPC: campaignData.maxTargetCPC,
          })
        }

        if (placement.orders === 0 && placement.clicks < 4) {
          indexesPlacementForPlacementModifireFromSettings.push(i)
        }

        if (
          placement.placement !== placementsEnum.AMAZON_BUSINESS &&
          placement.placement !== placementsEnum.AUDIENCE &&
          placement.limitedCPC > maxLimitedCPC
        ) {
          maxLimitedCPC = placement.limitedCPC
        }

        if (placement.limitedCPC < minLimitedCPC) {
          minLimitedCPC = placement.limitedCPC
          indexPlacementWithMinCPC = i
        }
      }
    }

    if (indexPlacementWithMinCPC !== null && placementsCopy.length > 0) {
      const placementWithMaxLimitedCPC = placementsCopy.reduce((max, placement) =>
        (placement.limitedCPC || 0) > (max.limitedCPC || 0) ? placement : max
      )
      const maxNewBidAdjustment =
        getNewBidAdjustment(placementWithMaxLimitedCPC, minLimitedCPC) || 0

      if (maxNewBidAdjustment > 900) {
        const cappedLimitedCPC = baseForTargetPercent(
          placementWithMaxLimitedCPC.limitedCPC || 0,
          900
        )
        placementsCopy[indexPlacementWithMinCPC].limitedCPC = cappedLimitedCPC
        minLimitedCPC = cappedLimitedCPC
      }
    }

    indexesPlacementForPlacementModifireFromSettings.forEach((index) => {
      const percentageFromSettings = determinePercentage(placementsCopy[index], {
        TOSPercentage: campaignData.TOSPercentage,
        PPPercentage: campaignData.PPPercentage,
        ROSPercentage: campaignData.ROSPercentage,
        ABPercentage: campaignData.ABPercentage,
        ADPercentage: campaignData.ADPercentage,
      })
      const modifier = 1 + percentageFromSettings / 100
      placementsCopy[index].limitedCPC =
        minLimitedCPC !== Number.POSITIVE_INFINITY
          ? minLimitedCPC * modifier
          : campaignData.defaultBid * modifier
    })

    return {
      placements: placementsCopy,
      minLimitedCPC,
      maxLimitedCPC,
    }
  }

  function baseForTargetPercent(value: number, targetPercent: number): number {
    return value / (1 + targetPercent / 100)
  }

  function calcTargetACOS(placements: IPlacementDataTable[]) {
    const placementsCopy: IPlacementDataTable[] = copy(placements)

    for (const placement of placementsCopy) {
      const campaign = campaignData.campaigns.find(
        (campaign) => campaign.campaignId === placement.campaignId
      )
      const targetAcos = determineTargetAcos(campaign)

      const MIN_CAMPAIGN_OVER_MARKET_CVR_RELATIVE_DIFF = 0.1
      const isExact = campaign?.matchType === matchTypesFromAmazon.EXACT
      const maxCampaignCvr = isExact ? Math.max(campaign.totalCvr || 0, campaign.asinCvr || 0) : 0
      const relativeDiffOverMarket = campaign?.marketCvr
        ? (maxCampaignCvr - campaign.marketCvr) / campaign.marketCvr
        : 0
      const exceedsMarketByMinRelativeDiff =
        !!campaign?.marketCvr &&
        relativeDiffOverMarket >= MIN_CAMPAIGN_OVER_MARKET_CVR_RELATIVE_DIFF
      const cvr =
        isExact && campaign.asinCvr && campaign.marketCvr && exceedsMarketByMinRelativeDiff
          ? maxCampaignCvr
          : placement.cvr || 0
      if (placement.placement === placementsEnum.TOP_OF_SEARCH) {
        const dynamicACOS = targetAcos?.dynamicTOStargetAcos.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcos = targetAcos?.dynamicTOSTarget
          ? dynamicACOS?.Acos || 0
          : targetAcos?.TOStargetAcos || 0
      }

      if (placement.placement === placementsEnum.REST_OF_SEARCH) {
        const dynamicACOS = targetAcos?.dynamicROStargetAcos.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcos = targetAcos?.dynamicROSTarget
          ? dynamicACOS?.Acos || 0
          : targetAcos?.ROStargetAcos || 0
      }

      if (placement.placement === placementsEnum.PRODUCT_PAGE) {
        const dynamicACOS = targetAcos?.dynamicPPtargetAcos.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcos = targetAcos?.dynamicPPTarget
          ? dynamicACOS?.Acos || 0
          : targetAcos?.PPtargetAcos || 0
      }

      if (placement.placement === placementsEnum.AUDIENCE) {
        const dynamicACOS = targetAcos?.dynamicADTargetACOS.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcos = targetAcos?.dynamicADTarget
          ? dynamicACOS?.Acos || 0
          : targetAcos?.ADtargetAcos || 0
      }

      if (placement.placement === placementsEnum.AMAZON_BUSINESS) {
        const dynamicACOS = targetAcos?.dynamicABTargetACOS.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcos = targetAcos?.dynamicABTarget
          ? dynamicACOS?.Acos || 0
          : targetAcos?.ABtargetAcos || 0
      }
    }

    return placementsCopy
  }

  function determineTargetAcos(campaign: ICampaign | undefined): IDefaultTargetAcos | undefined {
    const targetAcos = campaignData.defaultTargetAcos.find((targetAcos) => {
      if (
        campaign?.matchType === matchTypesFromAmazon.ASIN &&
        targetAcos.type === matchTypesEnum.ASIN
      ) {
        return true
      }

      if (
        campaign?.matchType === matchTypesFromAmazon.PHRASE &&
        targetAcos.type === matchTypesEnum.PHRASE
      ) {
        return true
      }

      if (
        campaign?.matchType === matchTypesFromAmazon.BROAD &&
        targetAcos.type === matchTypesEnum.BROAD
      ) {
        return true
      }

      if (
        campaign?.matchType === matchTypesFromAmazon.OTHER &&
        targetAcos.type === matchTypesEnum.ALL_OTHER
      ) {
        return true
      }

      if (
        campaign?.matchType === matchTypesFromAmazon.EXACT &&
        targetAcos.type === matchTypesEnum.EXACT
      ) {
        return true
      }

      if (
        campaign?.matchType === matchTypesFromAmazon.AUTO &&
        targetAcos.type === matchTypesEnum.AUTO
      ) {
        return true
      }

      if (
        campaign?.matchType === matchTypesFromAmazon.INCOMPLETE &&
        targetAcos.type === matchTypesEnum.ALL_OTHER
      ) {
        return true
      }

      const isRanking = campaign?.tags?.includes(campaignTagsEnum.RANKING)
      if (isRanking && targetAcos.type === matchTypesEnum.RANKING) {
        return true
      }

      return false
    })

    return targetAcos
  }

  function isUsableBaseCPC(value: number | undefined): value is number {
    return value !== undefined && Number.isFinite(value) && value > 0
  }

  function cpcFromBidAdjustment(baseCPC: number, adjustmentPercent: number): number {
    return Number((baseCPC * (1 + adjustmentPercent / 100)).toFixed(2))
  }

  function applyAudienceBidAdjustment(placement: IPlacementDataTable, baseCPC: number): void {
    if (!isUsableBaseCPC(baseCPC)) {
      placement.newBidAdjustment = 0
      return
    }

    const rawAdjustment = getNewBidAdjustment(placement, baseCPC) || 0
    const clampedAdjustment = Math.min(
      Math.max(rawAdjustment, AUDIENCE_MIN_BID_ADJUSTMENT),
      AUDIENCE_MAX_BID_ADJUSTMENT
    )

    placement.newBidAdjustment = clampedAdjustment
    if (clampedAdjustment !== rawAdjustment) {
      placement.limitedCPC = cpcFromBidAdjustment(baseCPC, clampedAdjustment)
    }
  }

  function calcNewBidAdjustmentForPlacement(
    placements: IPlacementDataTable[],
    minLimitedCPC: number,
    maxLimitedCPC?: number
  ): IPlacementDataTable[] {
    const placementsCopy: IPlacementDataTable[] = copy(placements)
    const audienceBaseCPC = isUsableBaseCPC(maxLimitedCPC) ? maxLimitedCPC : minLimitedCPC

    for (const placement of placementsCopy) {
      if (
        placement.placement === placementsEnum.AMAZON_BUSINESS ||
        placement.placement === placementsEnum.AUDIENCE
      ) {
        applyAudienceBidAdjustment(placement, audienceBaseCPC)
      } else {
        placement.newBidAdjustment = getNewBidAdjustment(placement, minLimitedCPC) || 0
      }
    }

    return placementsCopy
  }

  function isPlacementForCalcMetrics(placement: string, clicks: number) {
    return (
      (placement !== placementsEnum.AMAZON_BUSINESS ||
        (placement === placementsEnum.AMAZON_BUSINESS && clicks >= 4)) &&
      (placement !== placementsEnum.AUDIENCE ||
        (placement === placementsEnum.AUDIENCE && clicks >= 4))
    )
  }

  function calcAllMetrics(
    placementSubResult: IPlacementDataTable[],
    campaignId: number
  ): IPlacementDataTable[] {
    // не менять порядок вызова функций
    let placementsDataTable = calcTargetACOS(placementSubResult)
    const calculationLimitedCPC = calcLimitedCPCForPlacement(placementsDataTable, campaignId)
    placementsDataTable = calculationLimitedCPC.placements
    setMinLimitedCPCByCampaignIdMap((minLimitedCPCByCampaignIdMap) => {
      return {
        ...minLimitedCPCByCampaignIdMap,
        [String(campaignId)]: calculationLimitedCPC.minLimitedCPC,
      }
    })

    placementsDataTable = calcTargetCPCForPlacement(
      placementsDataTable,
      campaignId,
      calculationLimitedCPC.minLimitedCPC
    )
    placementsDataTable = calcBestCPCForPlacement(placementsDataTable)

    placementsDataTable = fillMinCPCPlacementWithoutBids(
      placementsDataTable,
      calculationLimitedCPC.minLimitedCPC
    )
    placementsDataTable = calcNewBidAdjustmentForPlacement(
      placementsDataTable,
      calculationLimitedCPC.minLimitedCPC,
      calculationLimitedCPC.maxLimitedCPC
    )
    setPlacementsDataForUpdate((prev) => {
      return prev.map((placement) => {
        if (placement.campaignId === campaignId) {
          return {
            campaignId,
            placements: placementsDataTable,
          }
        }

        return placement
      })
    })

    selectCampaignIfNeeded({
      campaignId,
      placements: placementsDataTable,
      campaignData,
      setSelectedCampaignIds,
    })
    return placementsDataTable
  }

  function calcMetricsBeforeUpdatedAcos(
    placementSubResult: IPlacementDataTable[],
    campaignId: number
  ): IPlacementDataTable[] {
    const calculationLimitedCPC = calcLimitedCPCForPlacement(placementSubResult, campaignId)
    let placementsDataTable = calculationLimitedCPC.placements
    setMinLimitedCPCByCampaignIdMap((minLimitedCPCByCampaignIdMap) => {
      return {
        ...minLimitedCPCByCampaignIdMap,
        [String(campaignId)]: calculationLimitedCPC.minLimitedCPC,
      }
    })

    placementsDataTable = calcTargetCPCForPlacement(
      placementsDataTable,
      campaignId,
      calculationLimitedCPC.minLimitedCPC
    )

    placementsDataTable = fillMinCPCPlacementWithoutBids(
      placementsDataTable,
      calculationLimitedCPC.minLimitedCPC
    )
    placementsDataTable = calcNewBidAdjustmentForPlacement(
      placementsDataTable,
      calculationLimitedCPC.minLimitedCPC,
      calculationLimitedCPC.maxLimitedCPC
    )
    setPlacementsDataForUpdate((prev) => {
      return prev.map((placement) => {
        if (placement.campaignId === campaignId) {
          return {
            campaignId,
            placements: placementsDataTable,
          }
        }

        return placement
      })
    })

    selectCampaignIfNeeded({
      campaignId,
      placements: placementsDataTable,
      campaignData,
      setSelectedCampaignIds,
    })
    return placementsDataTable
  }

  return {
    calcBestCPCForPlacement,
    calcTargetCPCForPlacement,
    calcLimitedCPCForPlacement,
    fillMinCPCPlacementWithoutBids,
    selectCampaignIfNeeded,
    calcNewBidAdjustmentForPlacement,
    calcAllMetrics,
    calcTargetACOS,
    determinePercentage,
    calcMetricsBeforeUpdatedAcos,
  }
}

export default usePlacementMetrics
